import React, { useEffect, useRef, useState } from 'react';
import Header from '../../../components/header';
import Layout from '../../../components/layout';
import { AgGridReact } from 'ag-grid-react';
import Icon from '../../../components/icons';
import Drawer from '../../../components/Drawer';
import { Dropdown, Menu } from '../../../components/Dropdown';
import Button from '../../../components/elements/button';
import { DATE_FORMAT, PAGE_SIZE } from '../../../_common/constant';
import Pagination from '../../../components/pagination';
import Search from '../../../components/search';
import { downloadExcel, downloadPDF, showLocalDate } from '../../../_common/functions';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import toast from 'react-hot-toast';
import { useDepreciationApi } from '../../../store/api/hooks/depreciationApiHook';
import { useSagesApi } from '../../../store/api/hooks/sageApiHook';
import { useAssetsApi } from '../../../store/api/hooks/assetsApiHook';
import { useFacilitiesApi } from '../../../store/api/hooks/facilitiesApiHook';
import Currency from '../../../components/currency';
import moment from 'moment';
import { filterConfigType } from '../../../_config/types';
import Filters from '../../../components/filters';

const DepreciationReportList: React.FC = () => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchParam, setSearchParam] = useState<string>('');
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [rowData, setrowData] = useState<any>(null);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [columnDefs, setColumnDefs] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const depreciationAPI = useDepreciationApi();
    const sageApi = useSagesApi();
    const assetsAPI = useAssetsApi();
    const facilitiesAPI = useFacilitiesApi();

    // Dropdown options state
    const [assetOptions, setAssetOptions] = useState<any[]>([]);
    const [facilityOptions, setFacilityOptions] = useState<any[]>([]);
    const [accountGroupOptions, setAccountGroupOptions] = useState<any[]>([]);
    const [accountOptions, setAccountOptions] = useState<any[]>([]);
    const [accmDepAccountOptions, setAccmDepAccountOptions] = useState<any[]>([]);

    // Filter states
    const filtersRef: any = useRef();
    const [filters, setFilters] = useState<any>({});
    const [filtersApplied, setFiltersApplied] = useState<any>({ generalFilter: false });
    const [filtersInitialized, setFiltersInitialized] = useState<boolean>(false);

    // Load filters from localStorage on component mount
    useEffect(() => {
        const savedFilters = localStorage.getItem('depreciationReportFilters');
        if (savedFilters) {
            try {
                const parsedFilters = JSON.parse(savedFilters);
                setFilters(parsedFilters);
            } catch (error) {
                console.error('Error parsing saved filters:', error);
                // Set default date range
                const currentYearLastDate = new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0];
                setFilters({
                    startDate: '2019-01-01',
                    endDate: currentYearLastDate
                });
            }
        } else {
            // Set default date range if no saved filters
            const currentYearLastDate = new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0];
            setFilters({
                startDate: '2019-01-01',
                endDate: currentYearLastDate
            });
        }
        setFiltersInitialized(true);
    }, []);

    // Save filters to localStorage when filters change
    useEffect(() => {
        if (Object.keys(filters).length > 0) {
            localStorage.setItem('depreciationReportFilters', JSON.stringify(filters));
        }
    }, [filters]);

    // Load dropdown options on component mount
    useEffect(() => {
        loadAssetOptions();
        loadFacilityOptions();
        loadAccountGroupOptions();
        loadAccountOptions(); // Load all accounts initially
        loadAccmDepAccountOptions();
    }, []);

    // API functions to load dropdown options
    const loadAssetOptions = () => {
        assetsAPI.getAssetsList({ pageSize: 0, pageNumber: 0 }, (resp: any) => {
            const options = resp.assetList?.map((asset: any) => ({
                label: `${asset.assetCode} - ${asset.name}`,
                value: asset.assetID
            })) || [];
            setAssetOptions(options);
        }, (error: any) => {
            console.error('Error loading assets:', error);
        });
    };

    const loadFacilityOptions = () => {
        facilitiesAPI.getFacilityList({ pageSize: 0, pageNumber: 0 }, (resp: any) => {
            const options = resp.facilityList?.map((facility: any) => ({
                label: facility.facilityName,
                value: facility.facilityID
            })) || [];
            setFacilityOptions(options);
        }, (error: any) => {
            console.error('Error loading facilities:', error);
        });
    };

    const loadAccountGroupOptions = () => {
        sageApi.getSyncAccountGroups({ pageSize: 0, pageNumber: 0 }, (resp: any) => {
            const options = resp.sageAccountGroupList?.map((group: any) => ({
                label: group.accountGroupName,
                value: group.accountGroupID
            })) || [];
            setAccountGroupOptions(options);
        }, (error: any) => {
            console.error('Error loading account groups:', error);
        });
    };

    const loadAccountOptions = (accountGroupID?: number) => {
        const payload: any = { pageSize: 0, pageNumber: 0 };
        if (accountGroupID) {
            payload.accountGroupID = accountGroupID;
        }
        
        sageApi.getUsedAccountList(payload, (resp: any) => {
            const options = resp.sageAccountList?.map((acc: any) => ({
                label: `${acc.accountNo} - ${acc.accountTitle}`,
                value: acc.accountID
            })) || [];
            setAccountOptions(options);
        }, (error: any) => {
            console.error('Error loading accounts:', error);
        });
    };

    const loadAccmDepAccountOptions = () => {
        sageApi.getUsedAccmDeprAccountList({ pageSize: 0, pageNumber: 0 }, (resp: any) => {
            const options = resp.sageAccountList?.map((acc: any) => ({
                label: `${acc.accountNo} - ${acc.accountTitle}`,
                value: acc.accountID
            })) || [];
            setAccmDepAccountOptions(options);
        }, (error: any) => {
            console.error('Error loading accumulated depreciation accounts:', error);
        });
    };

    const onGridReady = (params: any) => {
    };
    const onRowClicked = (event: any) => {
        if (!event.event.target.closest('input[type="checkbox"]')) {
            event.node.setSelected(false);
        }
    };

    // Function to generate month columns between start and end date
    const generateMonthColumns = (startDate: string, endDate: string) => {
        const months = [];
        const start = moment(startDate);
        const end = moment(endDate);

        let current = start.clone().startOf('month');
        while (current.isSameOrBefore(end, 'month')) {
            const monthKey = current.format('MMM-YY').toLowerCase(); // Format like "apr-25" (lowercase)
            const monthLabel = current.format('MMM YYYY'); // Display format like "Apr 2025"
            
            months.push({
                headerName: monthLabel,
                field: monthKey, // Use "apr-25" format as field name (lowercase)
                headerClass: 'bg-[#f0f0f0] ag-right-aligned-header',
                width: 120,
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
                headerTooltip: `Depreciation for ${monthLabel}`
            });
            
            current.add(1, 'month');
        }
        
        return months;
    };

    // Function to generate complete column definitions
    const generateColumnDefs = () => {
        const staticColumns = [
            { headerName: 'Asset Code', field: 'assetCode', headerClass: 'bg-[#f0f0f0]', pinned: 'left', width: 200, cellRenderer: titleCell, headerTooltip: 'Asset Code' },
            { headerName: 'Asset Name', field: 'name', headerClass: 'bg-[#f0f0f0]', pinned: 'left', minWidth: 200, cellRenderer: titleCell, headerTooltip: 'Asset Name' },
            { headerName: 'Facility Name', field: 'facilityName', headerClass: 'bg-[#f0f0f0]', pinned: 'left', width: 200, cellRenderer: titleCell, headerTooltip: 'Facility Name' },
            { headerName: 'Department', field: 'departmentName', headerClass: 'bg-[#f0f0f0]', pinned: 'left', width: 200, cellRenderer: titleCell, headerTooltip: 'Department' },
            { headerName: 'Is Disposed', field: 'isDisposed', headerClass: 'bg-[#f0f0f0]', pinned: 'left', width: 130, cellRenderer: titleCell, headerTooltip: 'isDisposed' },
            /* { headerName: 'Group Name', field: 'groupName', headerClass: 'bg-[#f0f0f0]', width: 200, cellRenderer: titleCell, headerTooltip: 'Group Name' }, */
            { headerName: 'Accumulated Depreciation Account Name', field: 'accumulatedDepreciationAccountName', headerClass: 'bg-[#f0f0f0]', width: 470, cellRenderer: titleCell, headerTooltip: 'Accumulated Depreciation Account Name' },
            { headerName: 'Purchase Cost', field: 'purchaseCost', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 200, 
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: (data: any) => <Currency currency="USD" value={data.value}></Currency>,
                headerTooltip: 'Purchase Cost' 
            },
            { headerName: 'Beginning Accumulated Depreciation', field: 'beginningAccumulatedDepreciation', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 300, 
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: (data: any) => <Currency currency="USD" value={data.value}></Currency>,
                headerTooltip: 'Beginning Accumulated Depreciation' 
            },
        ];

        let dynamicColumns: any[] = [];
        
        // Generate dynamic month columns if date filters are set
        if (filters.startDate && filters.endDate) {
            dynamicColumns = generateMonthColumns(filters.startDate, filters.endDate);
        }

        const endColumns = [
            { headerName: 'Ending Accumulated Depreciation', field: 'endingAccumulatedDepreciation', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 270, 
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: (data: any) => <Currency currency="USD" value={data.value}></Currency>,
                headerTooltip: 'Ending Accumulated Depreciation' 
            },
            { headerName: 'Net Book Value', field: 'netBookValue', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 150, 
                cellClass: 'ag-right-aligned-cell',
                cellRenderer: (data: any) => <Currency currency="USD" value={data.value}></Currency>,
                headerTooltip: 'Net Book Value' 
            },
        ];

        return [...staticColumns, ...dynamicColumns, ...endColumns];
    };

    // Update column definitions when filters change
    useEffect(() => {
        setColumnDefs(generateColumnDefs());
    }, [filters]);

    // Filter configuration
    const filterConfig: filterConfigType[] = [
        { name: 'Start Date', type: 'date', key: 'startDate' },
        { name: 'End Date', type: 'date', key: 'endDate' },
        { name: 'Asset', type: 'dropdown', key: 'assetID', options: assetOptions, isMulti: false },
        { name: 'Facility', type: 'dropdown', key: 'facilityID', options: facilityOptions, isMulti: false },
        { name: 'Account', type: 'dropdown', key: 'accountID', options: accountOptions, isMulti: false },
        { name: 'Accumulated Depreciation Account', type: 'dropdown', key: 'accmDepAccountID', options: accmDepAccountOptions, isMulti: false },
    ];

    const GetDepreciationReportList = () => {
        let payload: any = {
            pageSize: PAGE_SIZE,
            pageNumber: currentPage,
            searchParam: searchParam,
        };

        // Add filters to payload
        if (filters) {
            Object.keys(filters).map((key: any) => {
                if (filters[key]) {
                    payload[key] = filters[key];
                }
            });
        }

        setrowData(null);
        setIsLoading(true);
        
        depreciationAPI.GetDepreciationReport(payload, (resp: any) => {
            const depreciationList = resp.depreciationList ?? [];
            setrowData(depreciationList);
            setTotalRecords(resp.totalRecord);
            setIsLoading(false);
        }, (error: any) => {
            console.log('error =>', error);
            toast.error(error);
            setIsLoading(false);
        });

    };

    useEffect(() => {
        if (filtersInitialized) {
            GetDepreciationReportList();
        }
        
        const hasActiveFilters = filterConfig.some((filter) => filters[filter.key]);
        setFiltersApplied({
            generalFilter: hasActiveFilters,
        });
    }, [currentPage, searchParam, filters, filtersInitialized]);

    const ExportDepreciationReportExcel = async () => {
        let payload: any = {
            pageSize: null,
            pageNumber: null,
        };
        
        // Add filters to export payload
        if (filters) {
            Object.keys(filters).map((key: any) => {
                if (filters[key]) {
                    payload[key] = filters[key];
                }
            });
        }
        
        setIsExporting(true);
        depreciationAPI.ExportDepreciationReportcsv(
            payload,
            (resp: any) => {
                downloadExcel(resp, 'depreciation_report');
                toast.success('Depreciation report exported successfully!');
                setIsExporting(false);
            },
            (error: any) => {
                console.log("error =>", error);
                toast.error(error || 'Failed to export depreciation report');
                setIsExporting(false);
            }
        );
    };

    return (
        <Layout>
            <Header title="Depreciation Report" back="/reports" >
                <Header.Right>
                    {/* Excel export button is intentionally excluded as requested */}
                    <Button 
                        theme='primary' 
                        onClick={ExportDepreciationReportExcel}
                        disabled={isExporting}
                    >
                        {isExporting ? 'Exporting...' : 'Export Excel'}
                    </Button>
                </Header.Right>
            </Header>
            <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
                <Pagination
                    perPage={PAGE_SIZE}
                    totalRecords={totalRecords}
                    onPageChange={setCurrentPage}
                    currentPage={currentPage}
                />
                {/* <Search onSearch={setSearchParam} placeholder='Search by asset name or code' /> */}
            </div>
            <div className='bg-[#FAFAFA]'>
                <div className='flex gap-4 px-5 py-2 items-center'>
                    <Button theme='light' onClick={() => filtersRef.current.showModal()}>Filter <Icon name='filter' size={1.40} className={filtersApplied.generalFilter ? "text-blue-700" : ""} /></Button>
                    <p className='text-primary font-semibold text-sm cursor-pointer' onClick={() => { 
                        const currentYearLastDate = new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0];
                        setFilters({
                            startDate: '2019-01-01',
                            endDate: currentYearLastDate
                        });
                        localStorage.removeItem('depreciationReportFilters');
                    }} >Clear Filters</p>
                </div>

                <div className="ag-theme-alpine px-4 w-full h-1/2 relative">
                    {isLoading && (
                        <div className="absolute inset-0 bg-white bg-opacity-30 z-10 flex items-center justify-center">
                            <Loader />
                        </div>
                    )}
                    <AgGridReact
                        columnDefs={columnDefs} // Use dynamic columnDefs
                        rowData={isLoading ? null : (rowData || [])} // Don't pass empty array when loading
                        rowSelection="multiple"
                        onGridReady={onGridReady}
                        pagination={false}
                        domLayout="autoHeight"
                        onRowClicked={onRowClicked}
                        loadingOverlayComponent={Loader}
                        noRowsOverlayComponent={() => {
                            if (isLoading || !rowData) return null;
                            return (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center text-gray-500">
                                        <div className="text-lg font-medium">No Data Found</div>
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            </div>

            <Filters _ref={filtersRef} config={filterConfig} filters={filters} onUpdate={setFilters} />
        </Layout>
    );
};
export default DepreciationReportList;